import React, { useRef, useState } from "react";
import { Mic, Square, Loader2, Volume2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { log } from "../lib/logger";

function scoreColorFor(score) {
  if (score >= 90) return "bg-brand-mint";
  if (score >= 75) return "bg-brand-yellow";
  if (score >= 50) return "bg-brand-peach";
  return "bg-brand-pink text-white";
}

/**
 * SpeakBack: record learner's voice → send to /api/ai/speak-back → show score & feedback.
 * Props: cardId (required), onScore(result), pronounceRef (optional to trigger model audio play)
 */
export default function SpeakBack({ cardId, onScore, onPlayModel }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "speak.webm");
          const { data } = await api.post(`/ai/speak-back?card_id=${encodeURIComponent(cardId)}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setResult(data);
          onScore?.(data);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Couldn't score your audio");
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      // Auto-stop after 5s to keep clips short
      setTimeout(() => {
        if (mediaRef.current && mediaRef.current.state === "recording") {
          try { mediaRef.current.stop(); } catch (err) { log.debug("Auto-stop failed", err); }
        }
      }, 5000);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    try { mediaRef.current?.stop(); } catch (err) { log.debug("Stop failed", err); }
    setRecording(false);
  };

  const scoreColor = result ? scoreColorFor(result.score) : "";

  return (
    <div className="brutal-card-sm p-4 bg-brand-cream" data-testid="speak-back">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="brutal-badge bg-brand-blue text-white text-xs">Speak-back</div>
          <div className="text-sm font-bold text-brand-navy/70">Say it aloud to score your accent</div>
        </div>
        <div className="flex items-center gap-2">
          {onPlayModel && (
            <button
              type="button"
              onClick={onPlayModel}
              data-testid="speak-back-model-btn"
              className="brutal-btn-white text-xs py-2"
              title="Hear the model"
            >
              <Volume2 className="w-4 h-4" strokeWidth={3} />
            </button>
          )}
          {!recording && !uploading && (
            <button
              type="button"
              onClick={start}
              data-testid="speak-back-record-btn"
              className="brutal-btn-pink text-sm py-2"
            >
              <Mic className="w-4 h-4" strokeWidth={3} /> {result ? "Try again" : "Record"}
            </button>
          )}
          {recording && (
            <button
              type="button"
              onClick={stop}
              data-testid="speak-back-stop-btn"
              className="brutal-btn-pink text-sm py-2 animate-pulse"
            >
              <Square className="w-4 h-4" strokeWidth={3} /> Stop
            </button>
          )}
          {uploading && (
            <div className="brutal-badge bg-white">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring…
            </div>
          )}
        </div>
      </div>

      {result && (
        <div className="mt-4 space-y-2" data-testid="speak-back-result">
          <div className="flex items-center gap-3 flex-wrap">
            <div className={`brutal-badge text-base px-4 py-2 ${scoreColor}`} data-testid="speak-back-score">
              {result.score}%
            </div>
            <div className="font-bold text-brand-navy">{result.feedback}</div>
            {result.xp_gain > 0 && (
              <div className="brutal-badge bg-brand-mint text-xs">+{result.xp_gain} XP</div>
            )}
          </div>
          <div className="text-sm">
            <div><span className="font-bold uppercase text-brand-navy/50 text-xs">You said:</span> <span className="text-brand-navy">{result.transcription || "(nothing detected)"}</span></div>
            <div><span className="font-bold uppercase text-brand-navy/50 text-xs">Target:</span> <span className="text-brand-pink font-semibold">{result.expected}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
